<template>
  <div class="festival-admin">
    <festival-ops-nav active-path="/festival/decorate" />
    <header class="page-head">
      <div>
        <span class="eyebrow">FESTIVAL CAMPAIGN STUDIO</span>
        <div class="title-row">
          <h1>节日专题装修</h1>
          <el-tag :type="pageConfig.enabled ? 'success' : 'info'" size="small">
            {{ pageConfig.enabled ? '专题已启用' : '专题已关闭' }}
          </el-tag>
          <el-tag v-if="dirty" type="warning" size="small">草稿未保存</el-tag>
          <el-tag v-else-if="version" effect="plain" size="small">已发布 v{{ version }}</el-tag>
        </div>
        <p>配置用户端宣传图、主题色、分类、推荐编排和跳转。顶部不再叠加标题文案。</p>
      </div>
      <div class="head-actions">
        <el-button icon="el-icon-refresh" :loading="loading" @click="loadAll">重新加载</el-button>
        <el-button icon="el-icon-document-checked" :loading="saving" @click="saveDraft">保存草稿</el-button>
        <el-button type="primary" icon="el-icon-upload2" :loading="publishing" @click="publish">发布到用户端</el-button>
      </div>
    </header>

    <section class="scope-bar">
      <div class="scope-copy">
        <i class="el-icon-location-outline" />
        <span>当前小区</span>
        <strong>{{ communityName }}</strong>
        <code>{{ communityId }}</code>
      </div>
      <div class="scope-controls">
        <el-select v-model="clientType" size="small" @change="loadAll">
          <el-option v-for="item in clientOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-date-picker v-model="schedule" type="datetimerange" size="small" value-format="yyyy-MM-dd HH:mm:ss"
          start-placeholder="开始生效" end-placeholder="结束生效" range-separator="至" @change="dirty = true" />
        <el-switch v-model="pageConfig.enabled" active-text="启用专题" inactive-text="关闭专题" />
      </div>
    </section>

    <main class="workspace">
      <section class="editor-panel">
        <el-tabs v-model="activeTab" class="editor-tabs">
          <el-tab-pane label="页面装修" name="decorate">
            <div class="editor-section">
              <div class="section-head"><div><h2>专题基础信息</h2><p>用户端顶部只展示宣传图和订单入口，文案不再叠加在图上</p></div></div>
              <el-form label-position="top" class="form-grid">
                <el-form-item label="专题名称"><el-input v-model="configName" maxlength="40" /></el-form-item>
                <el-form-item label="主题标识"><el-input v-model="pageConfig.themeId" maxlength="40" /></el-form-item>
                <el-form-item label="主题主色"><el-color-picker v-model="pageConfig.theme.primaryColor" show-alpha /></el-form-item>
                <el-form-item label="主题辅助色"><el-color-picker v-model="pageConfig.theme.secondaryColor" show-alpha /></el-form-item>
                <el-form-item label="主题文字色"><el-color-picker v-model="pageConfig.theme.textColor" show-alpha /></el-form-item>
              </el-form>
              <el-form label-position="top"><el-form-item label="宣传图"><product-image-field v-model="pageConfig.hero.image.src" platform="TAOBAO_FLASH" /></el-form-item></el-form>
            </div>

            <div class="editor-section">
              <div class="section-head"><div><h2>倒计时</h2><p>用户端根据结束时间自动计算剩余天、时、分、秒</p></div></div>
              <el-form label-position="top" class="form-grid">
                <el-form-item label="倒计时提示"><el-input v-model="pageConfig.countdown.label" maxlength="24" /></el-form-item>
                <el-form-item label="倒计时结束时间"><el-date-picker v-model="pageConfig.countdown.endAt" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss+08:00" style="width:100%" /></el-form-item>
              </el-form>
            </div>

            <div class="editor-section">
              <div class="section-head">
                <div><h2>分类入口</h2><p>图标、名称和跳转行为均由配置下发</p></div>
                <el-button size="small" icon="el-icon-plus" @click="addCategory">新增分类</el-button>
              </div>
              <div class="category-edit-list">
                <article v-for="(item, index) in pageConfig.categories.items" :key="item.id" class="category-edit-card">
                  <div class="category-edit-card__head"><strong>分类 {{ index + 1 }}</strong><el-button type="text" class="danger-link" @click="removeCategory(index)">删除</el-button></div>
                  <el-form label-position="top" class="form-grid">
                    <el-form-item label="分类名称"><el-input v-model="item.title" maxlength="8" /></el-form-item>
                    <el-form-item label="无图片时图标"><el-input v-model="item.iconText" maxlength="2" /></el-form-item>
                    <el-form-item label="跳转类型"><el-select v-model="item.action.type" style="width:100%"><el-option v-for="option in actionOptions" :key="option.value" :label="option.label" :value="option.value" /></el-select></el-form-item>
                    <el-form-item :label="item.action.type === 'toast' ? '提示文案' : '跳转值'"><el-input v-model="item.action.value" placeholder="页面路径、链接或提示文案" /></el-form-item>
                  </el-form>
                  <el-form label-position="top"><el-form-item label="分类图标（可选）"><product-image-field v-model="item.image.src" platform="TAOBAO_FLASH" /></el-form-item></el-form>
                </article>
              </div>
            </div>

            <div class="editor-section editor-section--last">
              <div class="section-head"><div><h2>推荐区与优惠券</h2><p>商品内容在商品库维护，这里只控制编排和横幅</p></div></div>
              <el-form label-position="top" class="form-grid">
                <el-form-item label="推荐标题"><el-input v-model="pageConfig.recommendation.title" maxlength="12" /></el-form-item>
                <el-form-item label="推荐副标题"><el-input v-model="pageConfig.recommendation.subtitle" maxlength="24" /></el-form-item>
                <el-form-item label="查看更多跳转类型"><el-select v-model="pageConfig.recommendation.moreAction.type" style="width:100%"><el-option v-for="option in actionOptions" :key="option.value" :label="option.label" :value="option.value" /></el-select></el-form-item>
                <el-form-item label="查看更多跳转值"><el-input v-model="pageConfig.recommendation.moreAction.value" /></el-form-item>
                <el-form-item label="优惠券跳转类型"><el-select v-model="pageConfig.coupon.action.type" style="width:100%"><el-option v-for="option in actionOptions" :key="option.value" :label="option.label" :value="option.value" /></el-select></el-form-item>
                <el-form-item label="优惠券跳转值"><el-input v-model="pageConfig.coupon.action.value" /></el-form-item>
              </el-form>
              <el-form label-position="top"><el-form-item label="优惠券横幅"><product-image-field v-model="pageConfig.coupon.image.src" platform="TAOBAO_FLASH" /></el-form-item></el-form>
            </div>
          </el-tab-pane>

          <el-tab-pane label="商品编排" name="selection">
            <div class="editor-section editor-section--last">
              <div class="section-head">
                <div><h2>选择推荐商品</h2><p>勾选后按列表顺序展示，最多 8 个；商品资料从商品库实时读取</p></div>
                <div>
                  <el-tag type="warning" effect="plain">已选 {{ pageConfig.recommendation.productIds.length }} 个</el-tag>
                  <el-button size="small" icon="el-icon-goods" style="margin-left:8px" @click="$router.push('/festival/products')">去商品库</el-button>
                </div>
              </div>
              <div class="selection-grid">
                <label v-for="product in products" :key="product.productId" :class="['selection-card', { selected: isSelected(product.productId) }]">
                  <input type="checkbox" :checked="isSelected(product.productId)" @change="toggleProduct(product.productId)" />
                  <img :src="imageUrl(product.imageUrl)" :alt="product.title" />
                  <span>
                    <strong>{{ product.title }}</strong>
                    <small>¥{{ product.salePrice }} · {{ product.enabled === 'Y' ? '已上架' : '已下架' }} · {{ businessLabel(product.actionType) }}</small>
                  </span>
                  <i class="el-icon-check" />
                </label>
              </div>
              <el-empty v-if="!products.length" description="商品库暂无商品，请先到商品库新增" />
            </div>
          </el-tab-pane>
        </el-tabs>
      </section>

      <aside class="preview-panel">
        <div class="preview-title"><div><span class="eyebrow">LIVE PREVIEW</span><h2>用户端实时预览</h2></div><el-tag effect="plain" size="mini">{{ clientTypeLabel }}</el-tag></div>
        <div class="phone-shell" :style="previewTheme">
          <div class="phone-bar"><span>‹</span><strong>{{ configName || '节日专区' }}</strong><i /></div>
          <div class="phone-scroll">
            <div class="preview-hero" :style="heroStyle">
              <div class="preview-orders"><i class="el-icon-s-order" />我的订单</div>
            </div>
            <div class="preview-countdown">
              <span>{{ pageConfig.countdown.label }}</span>
              <strong>{{ remain.days }}</strong><i>天</i>
              <strong>{{ remain.hours }}</strong><i>:</i>
              <strong>{{ remain.minutes }}</strong><i>:</i>
              <strong>{{ remain.seconds }}</strong>
            </div>
            <div class="preview-categories">
              <div v-for="item in pageConfig.categories.items.slice(0, 4)" :key="item.id">
                <span>
                  <img v-if="item.image && item.image.src" :src="imageUrl(item.image.src)" :alt="item.title" />
                  <b v-else>{{ item.iconText }}</b>
                </span>
                <small>{{ item.title }}</small>
              </div>
            </div>
            <div class="preview-heading">
              <strong>{{ pageConfig.recommendation.title }}</strong>
              <span>{{ pageConfig.recommendation.subtitle }}</span>
            </div>
            <div class="preview-products">
              <article v-for="product in previewProducts.slice(0, 4)" :key="product.productId || product.id">
                <img :src="imageUrl(product.imageUrl || product.image && product.image.src)" :alt="product.title" />
                <strong>{{ product.title }}</strong>
                <small>{{ product.subtitle }}</small>
                <div><b>¥{{ product.salePrice }}</b><s v-if="product.originalPrice">¥{{ product.originalPrice }}</s></div>
              </article>
            </div>
            <img v-if="pageConfig.coupon.image.src" class="preview-coupon" :src="imageUrl(pageConfig.coupon.image.src)" alt="优惠券横幅" />
          </div>
        </div>
      </aside>
    </main>
  </div>
</template>

<script>
import ProductImageField from '@/components/upload/ProductImageField.vue'
import { getCommunity, getCommunityId } from '@/api/community/communityApi'
import { resolveImageUrl } from '@/utils/image-url'
import { getFestivalConfig, listFestivalProducts, publishFestivalConfig, saveFestivalDraft } from '@/api/life/festivalApi'
import FestivalOpsNav from './festivalNav.vue'
import {
  CLIENT_OPTIONS,
  PAGE_ACTION_OPTIONS,
  action,
  businessLabel,
  clientTypeLabel,
  countdownParts,
  createDefaultConfig,
  heroBackgroundStyle,
  image,
  normalizePageConfig,
  themeCssVars
} from './festivalConfig'

export default {
  name: 'FestivalDecorate',
  components: { FestivalOpsNav, ProductImageField },
  data() {
    return {
      loading: false,
      saving: false,
      publishing: false,
      dirty: false,
      ready: false,
      activeTab: 'decorate',
      clientType: 'ALL',
      clientOptions: CLIENT_OPTIONS,
      actionOptions: PAGE_ACTION_OPTIONS,
      configName: '中秋团圆 乐购好礼',
      version: 0,
      schedule: ['', ''],
      pageConfig: createDefaultConfig(),
      products: [],
      previewNow: Date.now()
    }
  },
  computed: {
    communityId() { return getCommunityId() || '-1' },
    communityName() { const current = getCommunity() || {}; return current.name || '物业公司默认范围' },
    clientTypeLabel() { return clientTypeLabel(this.clientType) },
    previewTheme() { return themeCssVars(this.pageConfig.theme) },
    heroStyle() {
      const src = this.pageConfig.hero.image && this.pageConfig.hero.image.src
      return heroBackgroundStyle(this.pageConfig.theme, src ? this.imageUrl(src) : '')
    },
    remain() { return countdownParts(this.pageConfig.countdown.endAt, this.previewNow) },
    previewProducts() {
      const ids = this.pageConfig.recommendation.productIds || []
      if (ids.length) {
        const map = Object.fromEntries(this.products.map(item => [item.productId, item]))
        return ids.map(id => map[id]).filter(Boolean)
      }
      return this.pageConfig.recommendation.products || []
    }
  },
  watch: { pageConfig: { deep: true, handler() { if (this.ready) this.dirty = true } } },
  created() { this.loadAll() },
  mounted() { this.previewTimer = setInterval(() => { this.previewNow = Date.now() }, 1000) },
  beforeDestroy() { if (this.previewTimer) clearInterval(this.previewTimer) },
  methods: {
    imageUrl: resolveImageUrl,
    businessLabel,
    async loadAll() {
      this.ready = false
      this.loading = true
      try { await Promise.all([this.loadConfig(), this.loadProducts()]) }
      finally { this.loading = false; this.$nextTick(() => { this.ready = true; this.dirty = false }) }
    },
    async loadConfig() {
      try {
        const res = await getFestivalConfig({ communityId: this.communityId, clientType: this.clientType })
        const row = res.data || {}
        this.pageConfig = normalizePageConfig(row.draft && Object.keys(row.draft).length ? row.draft : {})
        this.configName = row.configName || '中秋团圆 乐购好礼'
        this.version = Number(row.version || 0)
        this.schedule = [row.startTime || '', row.endTime || '']
        if (row.enabled) this.pageConfig.enabled = row.enabled === 'Y'
      } catch (error) { this.$message.error(error.message || '加载专题配置失败') }
    },
    async loadProducts() {
      try {
        const res = await listFestivalProducts({ page: 1, row: 200 })
        this.products = res.data || []
      } catch (error) { this.$message.error(error.message || '加载商品库失败') }
    },
    addCategory() {
      this.pageConfig.categories.items.push({ id: `category-${Date.now()}`, title: '新分类', iconText: '礼', image: image(), action: action() })
    },
    removeCategory(index) { this.pageConfig.categories.items.splice(index, 1) },
    isSelected(id) { return this.pageConfig.recommendation.productIds.includes(id) },
    toggleProduct(id) {
      const ids = this.pageConfig.recommendation.productIds
      const index = ids.indexOf(id)
      if (index >= 0) ids.splice(index, 1)
      else if (ids.length >= 8) this.$message.warning('最多选择 8 个推荐商品')
      else ids.push(id)
    },
    payload() {
      return {
        communityId: this.communityId,
        clientType: this.clientType,
        configName: this.configName,
        startTime: this.schedule && this.schedule[0] || '',
        endTime: this.schedule && this.schedule[1] || '',
        enabled: this.pageConfig.enabled,
        config: this.pageConfig
      }
    },
    async saveDraft(showMessage = true) {
      this.saving = true
      try {
        await saveFestivalDraft(this.payload())
        this.dirty = false
        if (showMessage) this.$message.success('草稿已保存，尚未影响用户端')
      } catch (error) {
        this.$message.error(error.message || '保存失败')
        throw error
      } finally { this.saving = false }
    },
    async publish() {
      try {
        await this.$confirm('发布后用户端将读取当前专题配置，确认继续？', '发布节日专题', { type: 'warning' })
        this.publishing = true
        await this.saveDraft(false)
        const res = await publishFestivalConfig(this.payload())
        this.version = Number(res.data && res.data.version || this.version + 1)
        this.$message.success('节日专题已发布到用户端')
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(error.message || '发布失败')
      } finally { this.publishing = false }
    }
  }
}
</script>

<style lang="scss" scoped>
.festival-admin { min-height: calc(100vh - 84px); padding: 24px; background: #f4f6f9; color: #243047; }
.page-head,.scope-bar,.workspace,.title-row,.head-actions,.scope-copy,.scope-controls,.section-head,.preview-title { display:flex; }
.page-head { align-items:flex-start; justify-content:space-between; gap:24px; margin-bottom:18px; }
.eyebrow { color:#d04431; font-size:11px; font-weight:800; letter-spacing:1.6px; }
.title-row { align-items:center; gap:10px; margin-top:5px; }
h1,h2,p { margin:0; } h1 { font-size:28px; } h2 { font-size:17px; } .page-head p,.section-head p { margin-top:6px; color:#7d8796; font-size:13px; }
.head-actions { gap:10px; }
.scope-bar { align-items:center; justify-content:space-between; gap:18px; margin-bottom:18px; padding:14px 18px; border:1px solid #e3e7ee; border-radius:12px; background:#fff; }
.scope-copy,.scope-controls { align-items:center; gap:12px; } .scope-copy i { color:#d04431;font-size:20px } .scope-copy span,.scope-copy code { color:#8b95a5;font-size:12px }.scope-copy code{padding:3px 7px;background:#f4f6f9;border-radius:5px}.scope-controls .el-select{width:150px}
.workspace { align-items:flex-start; gap:18px; }.editor-panel{min-width:0;flex:1;border:1px solid #e3e7ee;border-radius:14px;background:#fff}.editor-tabs ::v-deep .el-tabs__header{margin:0;padding:0 22px}.editor-tabs ::v-deep .el-tabs__content{overflow:visible}
.editor-section{padding:22px;border-bottom:1px solid #edf0f4}.editor-section--last{border-bottom:0}.section-head{align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:18px}.form-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:0 18px}.form-grid ::v-deep .el-form-item{margin-bottom:16px}
.category-edit-list{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}.category-edit-card{padding:16px;border:1px solid #e5e9ef;border-radius:12px;background:#fafbfc}.category-edit-card__head{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px}.danger-link{color:#e5574f!important}
.selection-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}.selection-card{position:relative;display:flex;min-width:0;align-items:center;gap:10px;padding:10px;border:2px solid #e5e9ef;border-radius:10px;cursor:pointer}.selection-card.selected{border-color:#d94a35;background:#fff7f3}.selection-card input{position:absolute;opacity:0}.selection-card img{width:58px;height:58px;flex:none;border-radius:8px;object-fit:cover}.selection-card span{min-width:0;display:flex;flex-direction:column}.selection-card strong,.selection-card small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.selection-card small{margin-top:5px;color:#929aaa}.selection-card>i{margin-left:auto;color:#d94a35;opacity:0}.selection-card.selected>i{opacity:1}
.preview-panel{position:sticky;top:18px;width:368px;flex:none;padding:16px;border:1px solid #e3e7ee;border-radius:14px;background:#fff}.preview-title{align-items:center;justify-content:space-between;margin-bottom:13px}.phone-shell{position:relative;width:326px;height:650px;margin:auto;overflow:hidden;border:8px solid #202331;border-radius:34px;background:var(--fe-primary,#cf2f23);box-shadow:0 18px 50px rgba(31,39,55,.18)}.phone-bar{display:grid;grid-template-columns:40px 1fr 40px;height:42px;align-items:center;background:var(--fe-primary,#cf2f23);color:var(--fe-text,#fff2d3);text-align:center}.phone-bar span{font-size:25px}.phone-scroll{height:calc(100% - 42px);overflow-y:auto;background:linear-gradient(180deg,var(--fe-primary,#cf2f23),var(--fe-secondary,#f27842));scrollbar-width:none}.preview-hero{position:relative;height:220px;background-position:center;background-size:cover}.preview-orders{position:absolute;top:10px;right:10px;display:flex;align-items:center;gap:4px;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.92);color:#243047;font-size:11px;font-weight:600}.preview-countdown{display:flex;height:43px;align-items:center;justify-content:center;gap:4px;margin:8px;border-radius:24px;background:var(--fe-text,#fff2d3);color:var(--fe-primary,#cf2f23);font-size:9px}.preview-countdown strong{padding:4px;border-radius:4px;background:var(--fe-primary,#cf2f23);color:#fff;font-size:12px}.preview-countdown i{font-style:normal}.preview-categories{display:grid;grid-template-columns:repeat(4,1fr);padding:9px 8px 14px}.preview-categories>div{display:flex;align-items:center;flex-direction:column;color:var(--fe-text,#fff2d3)}.preview-categories span{display:flex;width:45px;height:45px;align-items:center;justify-content:center;border:2px solid var(--fe-text,#fff2d3);border-radius:50%;background:var(--fe-primary,#cf2f23)}.preview-categories img{width:34px;height:34px;border-radius:50%;object-fit:cover}.preview-categories b{font-size:18px}.preview-categories small{margin-top:5px;font-size:9px}.preview-heading{display:flex;align-items:baseline;gap:10px;padding:9px 10px;color:var(--fe-text,#fff2d3)}.preview-heading strong{font:900 21px KaiTi,serif}.preview-heading span{font-size:9px}.preview-products{display:grid;grid-template-columns:repeat(2,1fr);gap:7px;padding:0 8px 10px}.preview-products article{overflow:hidden;padding-bottom:8px;border-radius:9px;background:#fff;color:#352d31}.preview-products img{width:100%;height:95px;object-fit:cover}.preview-products strong,.preview-products small,.preview-products div{display:block;padding:0 7px}.preview-products strong{margin-top:6px;font-size:11px}.preview-products small{min-height:24px;margin-top:3px;color:#8c776f;font-size:8px}.preview-products div{margin-top:3px}.preview-products b{color:var(--fe-primary,#cf2f23);font-size:14px}.preview-products s{margin-left:6px;color:#a49b98;font-size:8px}.preview-coupon{display:block;width:calc(100% - 16px);margin:2px 8px 14px}
@media(max-width:1280px){.preview-panel{width:330px}.phone-shell{width:292px}.selection-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:1050px){.workspace{display:block}.preview-panel{position:relative;top:auto;width:auto;margin-top:18px}.form-grid,.category-edit-list{grid-template-columns:1fr}.page-head,.scope-bar{flex-direction:column}.scope-controls{flex-wrap:wrap}}
</style>
